import { useNavigate, useParams } from 'react-router-dom';
import { fetchPloById, savePloDraft } from '../../../services/PloFiveService';
import { useQueryClient } from '@tanstack/react-query';
import { useSettings } from '../../../context/SettingContext';
import { useEffect, useState } from 'react';


interface PLORowFiveState {
  id: number;
  gender: string;             // ၁.ကျား၊ ၂.မ
  victimAge: string;          // သက်သေ၏အသက်
  victimOrWitnessName: string;// နစ်နာသူ/သက်သေအမည်
  interviewDate: string;      // သက်သေကြားနာမည့်နေ့
  ptiFirstDate: string;       // PTI ပထမအကြိမ် ပြုလုပ်သည့်နေ့
  ptiSecondDate: string;      // PTI ဒုတိယအကြိမ် ပြုလုပ်သည့်နေ့
  interviewerName: string;    // တွေ့ဆုံမေးမြန်းသူ အမည်
  interviewerRole: string;    // တွေ့ဆုံမေးမြန်းသူရာထူး (၁.ခရိုင်၊ ၂.ဒုခရိုင်၊ ၃.မြို့နယ်၊ ၄.ဒုမြို့နယ်၊ ၅.ဦးစီး၊ ၆.အခြား)
  judgmentType: string;       // စီရင်ချက်အမိန့် (၁.တရားသေလွတ်၊ ၂.တရားရှင်လွတ်၊ ၃.တရားစွဲအမိန့်)
  judgmentDate: string;       // အမိန့်ချသည့်ရက်စွဲ
  courtName: string;          // တရားရုံးအမည်
  appealOrRevision: string;   // ၁.အယူခံ၊ ၂.ပြင်ဆင်
  submissionDate: string;     // တင်သွင်းသည့်ရက်စွဲ
  remarks: string;            // မှတ်ချက်
}

interface PLOFormFiveState {
  officeId: string;
  officeName: string;
  rows: PLORowFiveState[];
}

export default function PLOFormFive() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>(); // Reads :id from route e.g. /plo-form-three/edit/:id

  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { settings } = useSettings();
  // const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  // const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];

  const [formData, setFormData] = useState<PLOFormFiveState>({
    officeId: '',
    officeName: '',
    rows: [
      {
        id: 1,
        gender: '',
        victimAge: '',
        victimOrWitnessName: '',
        interviewDate: '',
        ptiFirstDate: '',
        ptiSecondDate: '',
        interviewerName: '',
        interviewerRole: '',
        judgmentType: '',
        judgmentDate: '',
        courtName: '',
        appealOrRevision: '',
        submissionDate: '',
        remarks: ''
      }
    ]
  });

  // Fetch record when editing existing ID
  useEffect(() => {
  if (!id) return;

  const loadSingleRecord = async () => {
    try {
      setIsLoadingRecord(true);
      const res = await fetchPloById(id);
      const data = res?.data || res;

      if (data) {
        const formDetails = data.ploFormThree || data;

        // 1. Get officeId (reads data.officeId directly from flattened object)
        const currentOfficeId = data.officeId || data.ploFormThree?.officeId || data.originOfficeId || '';

        // 2. Fixed log: log the extracted currentOfficeId directly
        console.log("this is full data:", data);
        console.log("this is office id:", currentOfficeId); // Logs: "cmso7yfpk000owoc3v33olrvp"

        // 3. Find matching office name in dropdown options
        const matchedOffice = settings?.offices?.find(
          (o) => o.id === currentOfficeId
        );

        setFormData({
          officeId: currentOfficeId,
          officeName: matchedOffice ? matchedOffice.name : '',
          rows: [
            {
              id: 1,
              gender: formDetails.gender || '',
              victimAge: formDetails.victimAge || '',
              victimOrWitnessName: formDetails.victimOrWitnessName || '',
              interviewDate: formDetails.interviewDate || '',
              ptiFirstDate: formDetails.ptiFirstDate || '',
              ptiSecondDate: formDetails.ptiSecondDate || '',
              interviewerName: formDetails.interviewerName || '',
              interviewerRole: formDetails.interviewerRole || '',
              judgmentType: formDetails.judgmentType || '',
              judgmentDate: formDetails.judgmentDate || '',
              courtName: formDetails.courtName || '',
              appealOrRevision: formDetails.appealOrRevision || '',
              submissionDate: formDetails.submissionDate || '',
              remarks: formDetails.remarks || ''
            }
          ]
        });
      }
    } catch (error) {
      console.error('Failed to load record for editing:', error);
      alert('အချက်အလက်များ ဆွဲယူ၍ မရရှိပါ။');
    } finally {
      setIsLoadingRecord(false);
    }
  };

  loadSingleRecord();
}, [id, settings?.offices]); // Include settings so officeName updates when settings load

  // const handleOfficeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  // const selectedName = e.target.value;

  //   // Find matching office object from settings
  //   const matchedOffice = settings?.offices.find(
  //     (o) => o.name.trim().toLowerCase() === selectedName.trim().toLowerCase()
  //   );

  //   setFormData((prev) => ({
  //     ...prev,
  //     officeName: selectedName,
  //     officeId: matchedOffice ? matchedOffice.id : '', // Attach ID if matched, else reset
  //   }));
  // };

  const handleRowChange = (rowId: number, field: keyof PLORowFiveState, value: string) => {
    const updatedRows = formData.rows.map(row => {
      if (row.id === rowId) {
        return { ...row, [field]: value };
      }
      return row;
    });
    setFormData({ ...formData, rows: updatedRows });
  };

  const handleAddRow = () => {
    const newId = formData.rows.length > 0 ? Math.max(...formData.rows.map(r => r.id)) + 1 : 1;
    const newRow: PLORowFiveState = {
        id: newId,
        gender: '',
        victimAge: '',
        victimOrWitnessName: '',
        interviewDate: '',
        ptiFirstDate: '',
        ptiSecondDate: '',
        interviewerName: '',
        interviewerRole: '',
        judgmentType: '',
        judgmentDate: '',
        courtName: '',
        appealOrRevision: '',
        submissionDate: '',
        remarks: ''
    };
    setFormData({ ...formData, rows: [...formData.rows, newRow] });
  };

  const handleRemoveRow = (rowId: number) => {
    if (formData.rows.length === 1) {
      alert("အနည်းဆုံး အချက်အလက် Row တစ်ခု ရှိနေရပါမည်။");
      return;
    }
    setFormData({
      ...formData,
      rows: formData.rows.filter(row => row.id !== rowId)
    });
  };

  // Handle Save / Update Form
  const handleSaveForm = async () => {
    try {
      setIsSaving(true);

    const payload = formData.rows.map((row) => ({
      ...row,
      officeId: formData.officeId || undefined, // Sends officeId (or undefined/null if empty)
      id: id || undefined,
      status: 'DRAFT',
    }));

      // Pass `id` if editing existing record, or `null` if creating new
      await savePloDraft(id || null, payload);
      await queryClient.invalidateQueries({
        queryKey: ['ploFormFive'],
      });
      alert(id ? 'အချက်အလက်များ ပြင်ဆင်သိမ်းဆည်းပြီးပါပြီ။' : 'အချက်အလက်များကို အောင်မြင်စွာ သိမ်းဆည်းပြီးပါပြီ။');
      navigate(-1); // Go back to table view after saving
    } catch (error: any) {
      console.error('Failed to save PLO Form 3:', error);
      alert(error?.message || 'ဒေတာများ သိမ်းဆည်းရာတွင် အမှားအယွင်းရှိနေပါသည်။');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingRecord) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center font-sans text-sm text-slate-600">
        အချက်အလက်များ ပြန်လည်ဆွဲယူနေပါသည်...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 flex flex-col items-center justify-start font-serif text-black">
      
      {/* Top Controller */}
      <div className="w-full max-w-[1400px] mb-4 flex justify-between items-center font-sans">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-2 rounded shadow-sm transition"
          >
            ← နောက်သို့ (Back)
          </button>
          <h2 className="text-lg font-bold text-slate-800">
            {id ? 'ရှေ့ပြေးဥပဒေရုံးအမှုမှတ်တမ်း ပြင်ဆင်ရန် (Edit PLO Form 5)' : 'ရှေ့ပြေးဥပဒေရုံးအမှုမှတ်တမ်း (PLO Form 5)'}
          </h2>
        </div>

        <button
          type="button"
          onClick={() => alert("ပရင့်ထုတ်ရန် အစမ်းကြည့်ရှုခြင်း...")}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 py-2 rounded shadow transition"
        >
          📄 Print Preview
        </button>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-[1400px] bg-white shadow-xl border border-gray-300 p-8 space-y-6 overflow-x-auto">
        
        {/* Header Section */}
        <div className="flex justify-between items-start text-xs font-bold">
          {/* <div>
            <div className="flex items-center gap-2">
              <span>မှတ်တမ်းခေါင်းစဉ်/ရုံး -</span>
              <input
                type="text"
                value={formData.officeName}
                onChange={(e) => setFormData({ ...formData, officeName: e.target.value })}
                placeholder="ဖြည့်သွင်းရန်"
                className="border-b border-black bg-transparent focus:outline-none w-64 px-1 text-sm"
              />
            </div>
          </div> */}
          <div className="text-center space-y-1">
            {/* <div className="border border-black px-3 py-1 text-[11px]">PTI ပုံစံ</div> */}
            <div className="text-[11px] text-gray-500 font-normal pt-1">အမှုမှတ်တမ်းများဖြည့်သွင်းရန်ပုံစံ</div>
          </div>
        </div>

        {/* ဇယားကွက်ပိုင်း */}
        <div className="w-full overflow-x-auto border border-black">
          <table className="w-full text-left border-collapse text-[11px] min-w-[1200px]">
            <thead>
              {/* Row ခေါင်းစဉ်ကြီးများ */}
              <tr className="bg-slate-50 border-b border-black text-center align-middle font-bold">
                <th rowSpan={2} className="border-r border-black p-2 w-10">စဉ်</th>
                <th colSpan={4} className="border-r border-black p-1 bg-slate-100/70">နစ်နာသူ / သက်သေအချက်အလက်</th>
                <th colSpan={2} className="border-r border-black p-1 bg-slate-50">PTI ပြုလုပ်သည့်နေ့စွဲ</th>
                <th colSpan={2} className="border-r border-black p-1 bg-slate-100/70">တွေ့ဆုံမေးမြန်းသူအချက်အလက်</th>
                <th colSpan={3} className="border-r border-black p-1 bg-slate-50">စီရင်ချက်အမိန့်</th>
                <th colSpan={2} className="border-r border-black p-1 bg-slate-100/70">အယူခံ / ပြင်ဆင်</th>
                <th rowSpan={2} className="border-r border-black p-1 w-20">မှတ်ချက်</th>
                <th rowSpan={2} className="p-1 w-12 font-sans text-gray-500">Action</th>
              </tr>
              {/* Sub Headers ခေါင်းစဉ်ခွဲများ */}
              <tr className="bg-slate-50 border-b border-black text-center align-middle text-[10px] font-bold">
                {/* နစ်နာသူ / သက်သေအချက်အလက် */}
                <th className="border-r border-black p-1 w-12">၁။ကျား<br/>၂။မ</th>
                <th className="border-r border-black p-1 w-16">သက်သေ၏<br/>အသက်</th>
                <th className="border-r border-black p-1 w-24">နစ်နာသူ / သက်သေအမည်</th>
                <th className="border-r border-black p-1 w-20">သက်သေကြားနာ<br/>မည့်နေ့</th>
                {/* PTI ပြုလုပ်သည့်နေ့စွဲ */}
                <th className="border-r border-black p-1 w-20">PTI ပထမအကြိမ်<br/>ပြုလုပ်သည့်နေ့</th>
                <th className="border-r border-black p-1 w-20">PTI ဒုတိယအကြိမ်<br/>ပြုလုပ်သည့်နေ့</th>
                {/* တွေ့ဆုံမေးမြန်းသူအချက်အလက် */}
                <th className="border-r border-black p-1 w-24">တွေ့ဆုံမေးမြန်းသူ<br/>အမည်</th>
                <th className="border-r border-black p-1 w-28 text-left text-[9px] px-1 font-normal">
                  <b>၁။</b>ခရိုင်ဥပဒေအရာရှိ <b>၂။</b>ဒုခရိုင်<br/>
                  <b>၃။</b>မြို့နယ် <b>၄။</b>ဒုမြို့နယ်<br/>
                  <b>၅။</b>ဦးစီးအရာရှိ <b>၆။</b>အခြား
                </th>
                {/* စီရင်ချက်အမိန့် */}
                <th className="border-r border-black p-1 w-24 text-[9px] font-normal">
                  <b>၁။</b>တရားသေလွတ်သည်<br/>
                  <b>၂။</b>တရားရှင်လွတ်သည်<br/>
                  <b>၃။</b>တရားစွဲအမိန့်
                </th>
                <th className="border-r border-black p-1 w-16">အမိန့်ချသည့်<br/>ရက်စွဲ</th>
                <th className="border-r border-black p-1 w-20">တရားရုံး<br/>အမည်</th>
                {/* အယူခံ / ပြင်ဆင် */}
                <th className="border-r border-black p-1 w-14">၁။အယူခံ<br/>၂။ပြင်ဆင်</th>
                <th className="border-r border-black p-1 w-16">တင်သွင်းသည့်<br/>ရက်စွဲ</th>
              </tr>
            </thead>
            <tbody>
              {formData.rows.map((row, index) => (
                <tr key={row.id} className="border-b border-gray-400 hover:bg-slate-50 text-center align-middle">
                  {/* စဉ် */}
                  <td className="border-r border-black p-1 font-bold">{index + 1}</td>
                  
                  {/* ကျား / မ */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.gender} onChange={(e) => handleRowChange(row.id, 'gender', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* သက်သေ၏အသက် */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.victimAge} onChange={(e) => handleRowChange(row.id, 'victimAge', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* နစ်နာသူ / သက်သေအမည် */}
                  <td className="border-r border-black p-1 px-1">
                    <input type="text" value={row.victimOrWitnessName} onChange={(e) => handleRowChange(row.id, 'victimOrWitnessName', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* သက်သေကြားနာမည့်နေ့ */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.interviewDate} onChange={(e) => handleRowChange(row.id, 'interviewDate', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* PTI ပထမအကြိမ် */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.ptiFirstDate} onChange={(e) => handleRowChange(row.id, 'ptiFirstDate', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* PTI ဒုတိယအကြိမ် */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.ptiSecondDate} onChange={(e) => handleRowChange(row.id, 'ptiSecondDate', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* တွေ့ဆုံမေးမြန်းသူအမည် */}
                  <td className="border-r border-black p-1 px-1">
                    <input type="text" value={row.interviewerName} onChange={(e) => handleRowChange(row.id, 'interviewerName', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* တွေ့ဆုံမေးမြန်းသူရာထူး */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.interviewerRole} onChange={(e) => handleRowChange(row.id, 'interviewerRole', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" placeholder="1-6" />
                  </td>

                  {/* စီရင်ချက်အမိန့် */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.judgmentType} onChange={(e) => handleRowChange(row.id, 'judgmentType', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" placeholder="1-3" />
                  </td>

                  {/* အမိန့်ချသည့်ရက်စွဲ */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.judgmentDate} onChange={(e) => handleRowChange(row.id, 'judgmentDate', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* တရားရုံးအမည် */}
                  <td className="border-r border-black p-1 px-1">
                    <input type="text" value={row.courtName} onChange={(e) => handleRowChange(row.id, 'courtName', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* အယူခံ / ပြင်ဆင် */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.appealOrRevision} onChange={(e) => handleRowChange(row.id, 'appealOrRevision', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" placeholder="1-2" />
                  </td>

                  {/* တင်သွင်းသည့်ရက်စွဲ */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.submissionDate} onChange={(e) => handleRowChange(row.id, 'submissionDate', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* မှတ်ချက် */}
                  <td className="border-r border-black p-1 px-1">
                    <input type="text" value={row.remarks} onChange={(e) => handleRowChange(row.id, 'remarks', e.target.value)} className="w-full bg-transparent text-left focus:outline-none" />
                  </td>

                  {/* Delete Button */}
                  <td className="p-1 font-sans">
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(row.id)}
                      className="text-red-600 hover:text-red-800 font-medium text-xs focus:outline-none"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        {/* Add Row Button */}
        {!id && (
        <div className="flex justify-start font-sans">
          <button
            type="button"
            onClick={handleAddRow}
            className="bg-slate-800 hover:bg-slate-950 text-white text-xs font-medium px-4 py-2 rounded shadow transition flex items-center gap-1"
          >
            ➕ အတန်းအသစ်ထည့်ရန် (Add New Row)
          </button>
        </div>
      )}

        <div className="text-left text-[11px] font-bold text-gray-600 pt-4">
          * PTI အမှုမှတ်တမ်း
        </div>
      </div>

      {/* Save Button */}
      <div className="w-full max-w-[1400px] mt-4 flex justify-end font-sans">
        <button
          type="button"
          onClick={handleSaveForm}
          disabled={isSaving}
          className={`bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-6 py-2.5 rounded shadow-md transition flex items-center gap-2 ${
            isSaving ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isSaving ? 'သိမ်းဆည်းနေပါသည်...' : id ? '💾 ပြင်ဆင်ချက်များ သိမ်းဆည်းမည်' : '💾 ဒေတာများအားလုံးသိမ်းဆည်းမည်'}
        </button>
      </div>

    </div>
  );
}